import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Reveal, SectionHeading } from '../components/SectionHeading';
import { FAQSection } from '../components/home/FAQSection';
import { ContactForm } from '../components/ContactForm';
import { CTABand } from '../components/CTABand';
import { PROCESS, SERVICES } from '../data/siteData';
import { useSEO } from '../hooks/useSEO';

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useSEO(
    service ? `${service.name} | WorkLynx` : 'WorkLynx',
    service ? `${service.name} by WorkLynx — ${service.short}` : undefined
  );

  if (!service) return <Navigate to="/" replace />;
  const Icon = service.icon;

  return (
    <Layout>
      {/* Hero */}
      <section data-testid="service-hero" className="relative pt-32 pb-20 md:pb-28 bg-[#0A192F] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,102,204,0.22),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#4DA3FF] mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link> / <span>Services</span> / <span className="text-white">{service.name}</span>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="w-14 h-14 rounded-lg bg-[#4DA3FF]/15 border border-[#4DA3FF]/25 flex items-center justify-center mb-6">
                <Icon className="w-6 h-6 text-[#4DA3FF]" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-semibold text-white font-heading leading-tight" data-testid="service-title">{service.name}</h1>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed">{service.tagline}</p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 flex flex-wrap gap-2">
              {service.industries.map((ind) => (
                <span key={ind} className="text-xs font-medium px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-slate-300">{ind}</span>
              ))}
            </Reveal>
          </div>
          <Reveal delay={0.2} className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
            <img src={service.image} alt={service.name} className="w-full h-72 lg:h-80 object-cover" />
          </Reveal>
        </div>
      </section>

      {/* Overview + Challenges + Solutions */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <SectionHeading align="left" label="Overview" title={`What is ${service.name}?`} />
            <Reveal className="-mt-8 text-slate-600 leading-relaxed text-base" data-testid="service-overview">{service.overview}</Reveal>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-16">
            <Reveal className="p-8 rounded-xl border border-slate-200 bg-[#F8FAFC]">
              <h3 className="flex items-center gap-2.5 text-xl font-semibold text-[#0A192F] font-heading mb-6">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> Business Challenges
              </h3>
              <ul className="space-y-4">
                {service.challenges.map((c) => (
                  <li key={c} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" /> {c}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1} className="p-8 rounded-xl bg-[#0A192F] border border-white/10">
              <h3 className="flex items-center gap-2.5 text-xl font-semibold text-white font-heading mb-6">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" /> The WorkLynx Solution
              </h3>
              <ul className="space-y-4">
                {service.solutions.map((s) => (
                  <li key={s} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading label="Our Process" title="How We Deliver" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.1} className="relative p-7 bg-white border border-slate-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="text-4xl font-bold text-slate-100 font-heading absolute top-4 right-5">{p.step}</div>
                <div className="w-9 h-9 rounded-md bg-[#0066CC] text-white flex items-center justify-center text-sm font-bold mb-5">{i + 1}</div>
                <h3 className="font-semibold text-[#0A192F] font-heading text-[15px]">{p.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading label="Benefits" title={`Why Businesses Choose WorkLynx for ${service.name}`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.benefits.map((b, i) => (
              <Reveal key={b} delay={i * 0.05} className="flex items-center gap-3 p-5 border border-slate-200 rounded-lg hover:border-[#0066CC]/40 hover:shadow-sm transition-all">
                <CheckCircle2 className="w-5 h-5 text-[#0066CC] shrink-0" />
                <span className="text-sm font-medium text-[#0A192F]">{b}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FAQSection items={service.faqs} id="service-faq" />
      <CTABand title={`Need ${service.name}?`} subtitle="Tell us your requirement and get a tailored plan within one business day." />

      {/* Lead form */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <SectionHeading label="Get Started" title="Request a Consultation" />
          <Reveal className="bg-white border border-slate-200 rounded-xl p-7 md:p-10 shadow-sm">
            <ContactForm defaultService={service.name} />
          </Reveal>
        </div>
      </section>

      {/* Other services */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h3 className="text-xl font-semibold text-[#0A192F] font-heading mb-6">Explore Other Services</h3>
          <div className="flex flex-wrap gap-3">
            {SERVICES.filter((s) => s.slug !== slug).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-service-${s.slug}`}
                className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 text-sm font-medium text-slate-700 hover:border-[#0066CC] hover:text-[#0066CC] transition-colors">
                {s.name} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
